import { useState, useEffect, useRef } from "react"
import { Head, useForm, router, Link } from "@inertiajs/react"
import {
  Table,
  TableBody,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { QuestionRow, Question } from "@/components/QuestionRow"
import { cn } from "@/lib/utils"
import { ChevronLeft, ChevronRight, Save, ArrowLeft } from "lucide-react"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Checkbox } from "@/components/ui/checkbox"
import { Textarea } from "@/components/ui/textarea"

type Section = {
  id: string
  title: string
  questions: Question[]
}

type Category = {
  id: string
  title: string
  sections: Section[]
}

interface Patient {
  id: string
  first_name: string
  last_name: string
}

interface FormProps {
  categories: Category[]
  patient: Patient
  consultation?: {
    id: string
    consultation_date: string
    answers: Record<string, string[]>
    notes: string
    status: string
  }
}

// Debounce hook
function useDebounce<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value)

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedValue(value)
    }, delay)

    return () => {
      clearTimeout(timer)
    }
  }, [value, delay])

  return debouncedValue
}

export default function Form({ categories, patient, consultation }: FormProps) {
  const isEditing = !!consultation

  const { data, setData, post, put, processing } = useForm({
    patient_id: patient.id,
    consultation_date: consultation?.consultation_date || new Date().toISOString().split("T")[0],
    answers: consultation?.answers || ({} as Record<string, string[]>),
    notes: consultation?.notes || "",
    status: consultation?.status || "draft",
  })

  const [activeCategoryIndex, setActiveCategoryIndex] = useState(0)
  const activeCategory = categories[activeCategoryIndex]

  const debouncedAnswers = useDebounce(data.answers, 1000)
  const isFirstRender = useRef(true)

  // Auto-save effect
  useEffect(() => {
    // Skip initial render
    if (isFirstRender.current) {
      isFirstRender.current = false
      return
    }

    // Don't auto-save if we are submitting the final form
    if (processing) return

    const saveDraft = () => {
      const payload = { ...data, answers: debouncedAnswers, status: "draft" }
      
      const options = {
        preserveScroll: true,
        preserveState: true,
      }

      if (isEditing) {
        router.put(`/patients/${patient.id}/consultations/${consultation!.id}`, payload, options)
      } else {
        router.post(`/patients/${patient.id}/consultations`, payload, options)
      }
    }

    saveDraft()
    
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedAnswers])


  const handleToggle = (questionId: string, column: string) => {
    const current = data.answers[questionId] || []
    const next = current.includes(column)
      ? current.filter((c) => c !== column)
      : [...current, column]

    setData("answers", {
      ...data.answers,
      [questionId]: next,
    })
  }

  const handleTextGroupChange = (questionId: string, field: string, value: string) => {
    const current = data.answers[questionId] || []
    // We store text group answers as "Field: Value" strings in the array
    // Or better, we can map them by index if the order is fixed.
    // Given the current data structure is string[], let's use a convention "Field:Value"
    // But wait, the backend expects string[].
    // Let's use a structured approach: "Field: Value"
    
    const newAnswers = [...current]
    const prefix = `${field}: `
    const index = newAnswers.findIndex(a => a.startsWith(prefix))
    
    if (index >= 0) {
      if (value) {
        newAnswers[index] = `${prefix}${value}`
      } else {
        newAnswers.splice(index, 1)
      }
    } else if (value) {
      newAnswers.push(`${prefix}${value}`)
    }
    
    setData("answers", {
      ...data.answers,
      [questionId]: newAnswers,
    })
  }

  const handleCheckboxGroupChange = (questionId: string, option: string, checked: boolean) => {
    const current = data.answers[questionId] || []
    const next = checked
      ? [...current, option]
      : current.filter((c) => c !== option)

    setData("answers", {
      ...data.answers,
      [questionId]: next,
    })
  }

  const handleTextareaChange = (questionId: string, value: string) => {
    // Store as a single string in the array [value]
    setData("answers", {
      ...data.answers,
      [questionId]: [value],
    })
  }

  const getAnswerValue = (questionId: string, field: string) => {
    const answers = data.answers[questionId] || []
    const prefix = `${field}: `
    const answer = answers.find(a => a.startsWith(prefix))
    return answer ? answer.substring(prefix.length) : ""
  }

  const handleSubmit = () => {
    data.status = "completed"
    if (isEditing) {
      put(`/patients/${patient.id}/consultations/${consultation!.id}`)
    } else {
      post(`/patients/${patient.id}/consultations`)
    }
  }

  const nextStep = () => {
    if (activeCategoryIndex < categories.length - 1) {
      setActiveCategoryIndex(activeCategoryIndex + 1)
      window.scrollTo({ top: 0, behavior: "smooth" })
    }
  }

  const prevStep = () => {
    if (activeCategoryIndex > 0) {
      setActiveCategoryIndex(activeCategoryIndex - 1)
      window.scrollTo({ top: 0, behavior: "smooth" })
    }
  }

  return (
    <div className="container mx-auto py-8 max-w-5xl relative min-h-screen flex flex-col">
      <Head title={`${patient.first_name} ${patient.last_name} - ${data.consultation_date}`} />
      
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold tracking-tight">
          {patient.first_name} {patient.last_name} - {new Date(data.consultation_date).toLocaleDateString()}
        </h1>
        <Button variant="outline" asChild>
          <Link href={`/patients/${patient.id}`}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Retour au dossier
          </Link>
        </Button>
      </div>

      <div className="flex-1 pb-32">
        <div className="space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-500">
          <h2 className="text-2xl font-semibold mb-6 border-b pb-2">
            {activeCategory.title}
          </h2>
          
          <div className="space-y-8">
            {activeCategory.sections.map((section) => {
              // Group questions into chunks of consecutive matrix questions vs others
              const chunks: { type: "matrix" | "other"; questions: Question[] }[] = []
              let currentChunk: { type: "matrix" | "other"; questions: Question[] } | null = null

              section.questions.forEach((q) => {
                const isMatrix = !q.type || q.type === "matrix"
                const type = isMatrix ? "matrix" : "other"

                if (!currentChunk || currentChunk.type !== type) {
                  currentChunk = { type, questions: [] }
                  chunks.push(currentChunk)
                }
                currentChunk.questions.push(q)
              })

              return (
                <Card key={section.id}>
                  <CardHeader className="bg-muted/50 pb-4">
                    <CardTitle>{section.title}</CardTitle>
                  </CardHeader>
                  <CardContent className="p-0">
                    {chunks.map((chunk, chunkIndex) => (
                      <div key={chunkIndex} className={cn(chunk.type === "other" && "p-6")}>
                        {chunk.type === "matrix" ? (
                          <Table>
                            <TableHeader>
                              <TableRow>
                                <TableHead className="w-1/3">Symptôme</TableHead>
                                <TableHead className="text-center">Hyper</TableHead>
                                <TableHead className="text-center">Hypo</TableHead>
                                <TableHead className="text-center">Dyst</TableHead>
                                <TableHead className="text-center">Anergie</TableHead>
                                <TableHead className="text-center">Synd</TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {chunk.questions.map((question) => (
                                <QuestionRow
                                  key={question.id}
                                  question={question}
                                  answers={data.answers}
                                  onToggle={handleToggle}
                                />
                              ))}
                            </TableBody>
                          </Table>
                        ) : (
                          <div className="space-y-6">
                            {chunk.questions.map((question) => (
                              <div key={question.id} className="space-y-2">
                                <Label className="text-base font-semibold">{question.label}</Label>
                                
                                {question.type === "text_group" && question.fields && (
                                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                    {question.fields.map((field) => (
                                      <div key={field} className="space-y-1">
                                        <Label htmlFor={`${question.id}_${field}`} className="text-sm font-normal text-muted-foreground">
                                          {field}
                                        </Label>
                                        <Input
                                          id={`${question.id}_${field}`}
                                          value={getAnswerValue(question.id, field)}
                                          onChange={(e) => handleTextGroupChange(question.id, field, e.target.value)}
                                        />
                                      </div>
                                    ))}
                                  </div>
                                )}

                                {question.type === "checkbox_group" && Array.isArray(question.options) && (
                                  <div className="flex flex-wrap gap-4 pt-2">
                                    {question.options.map((option) => (
                                      <div key={option} className="flex items-center space-x-2">
                                        <Checkbox
                                          id={`${question.id}_${option}`}
                                          checked={(data.answers[question.id] || []).includes(option)}
                                          onCheckedChange={(checked) => handleCheckboxGroupChange(question.id, option, checked as boolean)}
                                        />
                                        <Label htmlFor={`${question.id}_${option}`} className="font-normal">
                                          {option}
                                        </Label>
                                      </div>
                                    ))}
                                  </div>
                                )}

                                {question.type === "textarea" && (
                                  <Textarea
                                    value={(data.answers[question.id] || [])[0] || ""}
                                    onChange={(e) => handleTextareaChange(question.id, e.target.value)}
                                    className="min-h-[100px]"
                                    placeholder={question.fields?.join(", ")}
                                  />
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </CardContent>
                </Card>
              )
            })}
          </div>
        </div>
      </div>

      {/* Footer Navigation */}
      <div className="fixed bottom-0 left-0 right-0 bg-background border-t p-4 z-50">
        <div className="container max-w-5xl mx-auto flex justify-between items-center">
          <Button
            variant="outline"
            onClick={prevStep}
            disabled={activeCategoryIndex === 0}
            className={cn(activeCategoryIndex === 0 && "invisible")}
          >
            <ChevronLeft className="w-4 h-4 mr-2" />
            Précédent
          </Button>

          {/* Dot Navigation */}
          <div className="flex justify-center items-center gap-4">
            {categories.map((category, index) => (
              <button
                key={category.id}
                onClick={() => setActiveCategoryIndex(index)}
                className="group flex flex-col items-center gap-1 focus:outline-none"
              >
                <div
                  className={cn(
                    "w-3 h-3 rounded-full transition-all duration-300",
                    activeCategoryIndex === index
                      ? "bg-primary scale-125"
                      : "bg-muted-foreground/30 group-hover:bg-primary/50"
                  )}
                />
              </button>
            ))}
          </div>

          <div className="flex gap-2">
            {activeCategoryIndex === categories.length - 1 ? (
              <Button 
                onClick={handleSubmit} 
                disabled={processing}
                size="lg"
              >
                <Save className="w-4 h-4 mr-2" />
                Terminer
              </Button>
            ) : (
              <Button onClick={nextStep} size="lg">
                Suivant
                <ChevronRight className="w-4 h-4 ml-2" />
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
